import { AppBar, Box, Button, Toolbar } from '@mui/material';
import { inject, observer } from 'mobx-react';
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import api from '../../api';
import ReceiveMethodEnum from '../../enums/ReceiveMethodEnum';
import CurrencyDisplay from '../common/CurrencyDisplay';
import Progress from '../common/Progress';

@inject('commonStore', 'menuStore', 'cartStore', 'userStore', 'orderStore')
@observer
class BottomPoint extends Component {
  componentDidMount() {
    // 페이지 접속 후에는 결제 상태 초기화
    this.props.orderStore.setIsPaying(false);
  }

  // 결제하기 버튼 클릭
  handleClickPay = async () => {
    const { t, commonStore, menuStore, userStore, orderStore, cartStore } = this.props;

    try {
      // 결제 버튼 비활성화 (중복결제방지)
      orderStore.setIsPaying(true);

      // (일본) 배달인 경우 주소 검사
      if (
        menuStore.storeData.culture === 'ja-JP' &&
        cartStore.cartData.receiveMethodCode === ReceiveMethodEnum.DELIVERY &&
        !userStore.userData.addressDetail
      ) {
        commonStore.setSnackbar(true, t('order.error.addressRequired'));
        return;
      }

      if (
        menuStore.storeData.isPointUseable &&
        orderStore.usePoint > 0 &&
        userStore.userPoint < menuStore.storeData.useableMinimumPoint
      ) {
        commonStore.setSnackbar(
          true,
          t('order.error.pointMinLimit', { n: Number(menuStore.storeData.useableMinimumPoint).toLocaleString() }),
        );
        return;
      }

      if (orderStore.paymentAmount > 0 && orderStore.paymentAmount < 100 && menuStore.storeData.culture !== 'ja-JP') {
        commonStore.setSnackbar(true, t('payment.error.minAmount'));
        return;
      }

      // 주문 저장 전 사전 처리 (NiceVan 등 서명이 필요한 PG사에서 사용)
      if (this.props.beforePay) {
        await this.props.beforePay();
      }

      // 주문 저장
      const authData = await api
        .addOrder(orderStore.orderData)
        .then(response => response.data)
        .catch(error => {
          commonStore.setSnackbar(true, t('common.error.occurred'));
          return;
        });
      orderStore.setOrderAuthData(authData);

      // 결제 금액이 0인 경우
      if (orderStore.paymentAmount === 0) {
        await this.payByPointOnly();
      } else if (orderStore.paymentMethod === '0') {
        // 이지웰페이 결제
        await this.payByEzwelPay();
      } else {
        // PG사별 결제
        this.props.pay();
      }
    } catch (error) {
      // beforePay 취소(서명 취소 등)는 사용자가 의도한 동작이므로 에러 처리 생략
      if (error?.isCancelled) return;

      if (error.response?.data?.errorCode === 9) {
        commonStore.setSnackbar(true, this.getExceedItemMessage(error.response.data.message));
        return;
      }

      commonStore.setError(error);
      throw error;
    } finally {
      orderStore.setIsPaying(false);
    }
  };

  payByPointOnly = async () => {
    const { history, location, match, menuStore, orderStore } = this.props;

    try {
      // 주문 포인트 결제 완료 처리
      const encryptedOrderId = await api
        .payOrderByPointOnly({
          companyId: menuStore.storeData.companyId,
          storeCode: menuStore.storeData.storeCode,
          orderId: orderStore.orderAuthData.orderId,
        })
        .then(response => response.data);

      // 마장동 주문여부에 따라 완료 페이지 분기하여 이동
      if (orderStore.liteOrder) {
        history.replace(
          `/${match.params.companyId}/${match.params.storeCode}/lite-orders/${encryptedOrderId}/complete${location.search}`,
        );
      } else {
        history.replace(
          `/${match.params.companyId}/${match.params.storeCode}/orders/${encryptedOrderId}/complete${location.search}`,
        );
      }
    } catch (error) {
      const searchParams = new URLSearchParams(window.location.search);
      if (error.response?.data?.errorCode) searchParams.set('errorCode', error.response.data.errorCode);
      history.replace(`/${match.params.companyId}/${match.params.storeCode}/errors/payment?${searchParams.toString()}`);
    }
  };

  payByEzwelPay = async () => {
    const { history, location, match, menuStore, orderStore } = this.props;

    try {
      // 이지웰페이 결제 준비 API 실행
      const response = await api.readyEzwelPay({
        companyId: menuStore.storeData.companyId,
        storeCode: menuStore.storeData.storeCode,
        orderId: orderStore.orderAuthData.orderId,
      });
      if (menuStore.storeData.ezwelPaySchemeType === '1') {
        window.location.href = response.data.next_redirect_mobile_url_intent;
      } else {
        window.location.href = response.data.next_redirect_mobile_url;
      }
    } catch (error) {
      console.error(error);
      history.replace(`/${match.params.companyId}/${match.params.storeCode}/errors/payment${location.search}`);
    }
  };

  getExceedItemMessage(exceedItems) {
    const { t } = this.props;
    let message = '';
    exceedItems.forEach(item => {
      const menu = this.props.menuStore.storeData.menus.find(menu => menu.itemCode === item.itemCode);
      message +=
        t('order.error.purchaseLimit', {
          name: menu.name,
          limit: item.purchaseLimitCount,
          qty: item.avaliableQty,
        }) + '\n';
    });
    return message;
  }

  render() {
    const { t, menuStore, cartStore, orderStore } = this.props;
    if (!menuStore.storeData || !cartStore.cartData) return <Progress />;
    return (
      <AppBar position="fixed" color="primary" className="bottom">
        <Toolbar variant="dense" style={{ padding: '0' }}>
          <Button fullWidth onClick={this.handleClickPay} disabled={orderStore.isPaying} className="bottom-button">
            <Box className="bottom-button-title">{t('payment.button.pay')}</Box>
            <Box bgcolor="secondary.main" className="bottom-button-amount">
              <CurrencyDisplay amount={orderStore.paymentAmount} size="large" />
            </Box>
          </Button>
        </Toolbar>
      </AppBar>
    );
  }
}

export default withRouter(withTranslation()(BottomPoint));
